import { collection, doc, getDocs, setDoc, updateDoc, query, where } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { format } from "date-fns";

export type NonCheckInReasonEntry = {
  date: string; // yyyy-MM-dd
  reason: string;
  userId: string;
  timestamp: string;
  approvalStatus?: "Pending" | "Approved" | "Rejected";
  reviewedBy?: string;
  reviewedAt?: string;
  type?: string; // "auto checkout" for auto-checkout days
};

const monthKeyFromDate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const getMonthKeysForYear = (year: number): string[] =>
  Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);

/**
 * Fetch all non-check-in reason entries for a user for a given year.
 * Path: organization/{orgId}/NonCheckInReason/{userId}/{YYYY-MM}/{yyyy-MM-dd}
 * Returned map is keyed by date (yyyy-MM-dd).
 */
export const fetchNonCheckInReasonsForYear = async (
  organizationId: string,
  userId: string,
  year: number
): Promise<Record<string, NonCheckInReasonEntry>> => {
  const monthKeys = getMonthKeysForYear(year);

  const snapshots = await Promise.all(
    monthKeys.map((mk) => {
      const path = `organization/${organizationId}/NonCheckInReason/${userId}/${mk}`;
      return getDocs(collection(db, path))
        .catch((err) => {
          console.error(`Failed to fetch non-check-in for ${userId}/${mk}:`, err.message);
          return null;
        });
    })
  );

  const result: Record<string, NonCheckInReasonEntry> = {};
  snapshots.forEach((snap) => {
    if (!snap) return;
    snap.docs.forEach((d) => {
      result[d.id] = d.data() as NonCheckInReasonEntry;
    });
  });

  return result;
};

/**
 * Create/update the reason a user didn't check in on a given date.
 * One entry per day (doc id = yyyy-MM-dd) inside that day's month subcollection.
 */
export const saveNonCheckInReason = async (
  organizationId: string,
  userId: string,
  date: Date,
  reason: string
): Promise<void> => {
  const dateKey = format(date, "yyyy-MM-dd");
  const monthKey = monthKeyFromDate(date);
  const entryRef = doc(
    db,
    `organization/${organizationId}/NonCheckInReason/${userId}/${monthKey}/${dateKey}`
  );
  const entry: NonCheckInReasonEntry = {
    date: dateKey,
    reason,
    userId,
    timestamp: new Date().toISOString(),
    approvalStatus: "Pending",
  };
  await setDoc(entryRef, entry);
};

/**
 * Update the approval status of a non-check-in reason entry.
 */
export const updateNonCheckInReasonStatus = async (
  organizationId: string,
  userId: string,
  dateKey: string,
  status: "Approved" | "Rejected",
  reviewedBy: string,
  type?: string
): Promise<void> => {
  const [year, month] = dateKey.split("-");
  const monthKey = `${year}-${month}`;
  const entryRef = doc(
    db,
    `organization/${organizationId}/NonCheckInReason/${userId}/${monthKey}/${dateKey}`
  );
  const updateData: Record<string, any> = {
    approvalStatus: status,
    reviewedBy,
    reviewedAt: new Date().toISOString(),
  };
  if (type) {
    updateData.type = type;
  }
  await updateDoc(entryRef, updateData);
};

/**
 * Fetch all pending non-check-in reason entries for multiple users for a given month.
 * Path: organization/{orgId}/NonCheckInReason/{userId}/{YYYY-MM}
 * Returns entries grouped by userId.
 */
export const fetchPendingNonCheckInReasonsForUsers = async (
  organizationId: string,
  userIds: string[],
  year: number,
  month: number // 1-12
): Promise<Record<string, (NonCheckInReasonEntry & { _monthKey: string; _docId: string })[]>> => {
  const monthKey = `${year}-${String(month).padStart(2, "0")}`;
  const result: Record<string, (NonCheckInReasonEntry & { _monthKey: string; _docId: string })[]> = {};

  await Promise.all(
    userIds.map(async (userId) => {
      try {
        const q = query(
          collection(db, `organization/${organizationId}/NonCheckInReason/${userId}/${monthKey}`),
          where("approvalStatus", "==", "Pending")
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          result[userId] = snap.docs.map((d) => ({
            ...d.data(),
            _monthKey: monthKey,
            _docId: d.id,
          })) as (NonCheckInReasonEntry & { _monthKey: string; _docId: string })[];
        }
      } catch (err) {
        console.error(`Error fetching pending non-checkin reasons for ${userId}:`, err);
      }
    })
  );

  return result;
};
